import { useCallback, useEffect, useState } from "react";
import { TextField, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CalendarPicker } from "@mui/x-date-pickers/CalendarPicker";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import {
  addMonths,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  startOfDay,
  subDays,
  subMonths,
} from "date-fns";
import ar from "date-fns/locale/ar-SA";
import enUS from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
import ptBR from "date-fns/locale/pt-BR";
import intl from "react-intl-universal";
import { formatFilterDate } from "./gridFilterUtils";

const DATE_FNS_LOCALES = {
  "pt-BR": ptBR,
  "en-US": enUS,
  "es-ES": es,
  "ar-AE": ar,
};

const DATE_PRESETS = {
  TODAY: "today",
  LAST_7_DAYS: "7days",
  LAST_30_DAYS: "30days",
  CUSTOM: "custom",
};

const getDateFnsLocale = () => {
  const currentLocale = intl.getInitOptions()?.currentLocale || "pt-BR";
  return DATE_FNS_LOCALES[currentLocale] || ptBR;
};

const RangePickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "isRangeStart" && prop !== "isRangeEnd" && prop !== "isInRange",
})(({ isRangeStart, isRangeEnd, isInRange }) => ({
  ...(isInRange && {
    backgroundColor: "rgba(231, 167, 63, 0.18)",
    borderRadius: 0,
    "&:hover, &:focus": {
      backgroundColor: "rgba(231, 167, 63, 0.28)",
    },
  }),
  ...((isRangeStart || isRangeEnd) && {
    backgroundColor: "#e7a73f !important",
    color: "#1a1a1a !important",
    fontWeight: 600,
    "&:hover, &:focus": {
      backgroundColor: "#ecb24e !important",
    },
  }),
  ...(isRangeStart &&
    !isRangeEnd && {
      borderTopLeftRadius: "50%",
      borderBottomLeftRadius: "50%",
    }),
  ...(isRangeEnd &&
    !isRangeStart && {
      borderTopRightRadius: "50%",
      borderBottomRightRadius: "50%",
    }),
  ...(isRangeStart &&
    isRangeEnd && {
      borderRadius: "50%",
    }),
}));

const renderRangeDay = (dateFrom, dateTo) => (day, _selectedDays, pickersDayProps) => {
  const normalizedDay = startOfDay(day);
  const rangeStart = dateFrom ? startOfDay(dateFrom) : null;
  const rangeEnd = dateTo ? startOfDay(dateTo) : null;
  const isRangeStart = rangeStart ? isSameDay(normalizedDay, rangeStart) : false;
  const isRangeEnd = rangeEnd ? isSameDay(normalizedDay, rangeEnd) : false;
  const isInRange =
    rangeStart &&
    rangeEnd &&
    !isRangeStart &&
    !isRangeEnd &&
    isWithinInterval(normalizedDay, { start: rangeStart, end: rangeEnd });

  return (
    <RangePickersDay
      {...pickersDayProps}
      day={day}
      disableMargin={isInRange || isRangeStart || isRangeEnd}
      isRangeStart={isRangeStart}
      isRangeEnd={isRangeEnd}
      isInRange={isInRange}
    />
  );
};

const buildRangeSummary = (dateFrom, dateTo) => {
  if (dateFrom && dateTo) {
    return `${formatFilterDate(dateFrom)} — ${formatFilterDate(dateTo)}`;
  }
  if (dateFrom) {
    return `${formatFilterDate(dateFrom)} — …`;
  }
  return intl.get("extract.selectDateRange");
};

const isDateInBounds = (date, minDate, maxDate) => {
  if (minDate && isBefore(date, startOfDay(minDate))) return false;
  if (maxDate && isAfter(date, startOfDay(maxDate))) return false;
  return true;
};

export const getTodayDateRange = () => {
  const today = startOfDay(new Date());
  return { dateFrom: today, dateTo: today };
};

export const getLast7DaysDateRange = () => {
  const today = startOfDay(new Date());
  return { dateFrom: subDays(today, 6), dateTo: today };
};

export const getLast30DaysDateRange = () => {
  const today = startOfDay(new Date());
  return { dateFrom: subDays(today, 29), dateTo: today };
};

const detectActivePreset = (dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return null;

  const today = startOfDay(new Date());
  const from = dateFrom ? startOfDay(dateFrom) : null;
  const to = dateTo ? startOfDay(dateTo) : null;

  if (from && to && isSameDay(from, today) && isSameDay(to, today)) {
    return DATE_PRESETS.TODAY;
  }
  if (from && to && isSameDay(from, subDays(today, 6)) && isSameDay(to, today)) {
    return DATE_PRESETS.LAST_7_DAYS;
  }
  if (from && to && isSameDay(from, subDays(today, 29)) && isSameDay(to, today)) {
    return DATE_PRESETS.LAST_30_DAYS;
  }
  return DATE_PRESETS.CUSTOM;
};

const PRESET_OPTIONS = [
  { id: DATE_PRESETS.TODAY, labelKey: "extract.periodToday" },
  { id: DATE_PRESETS.LAST_7_DAYS, labelKey: "extract.period7Days" },
  { id: DATE_PRESETS.LAST_30_DAYS, labelKey: "extract.period30Days" },
  { id: DATE_PRESETS.CUSTOM, labelKey: "extract.periodCustom" },
];

const DATE_INPUT_PLACEHOLDER = "dd/mm/yyyy";

const DatePickerField = ({ label, ...params }) => (
  <label className="extract-date-range-calendar__date-field">
    <span className="extract-date-range-calendar__date-label">{label}</span>
    <TextField
      {...params}
      fullWidth
      size="small"
      variant="outlined"
      className="extract-date-range-calendar__date-picker"
      inputProps={{
        ...params.inputProps,
        placeholder: DATE_INPUT_PLACEHOLDER,
      }}
      InputProps={{
        ...params.InputProps,
        className: "extract-date-range-calendar__date-picker-input",
      }}
    />
  </label>
);

const PresetChipsRow = ({
  presets,
  activePreset,
  onPresetClick,
  onClear,
  showClear = true,
  className = "",
}) => (
  <div
    className={`extract-date-range-calendar__presets ${className}`.trim()}
    role="group"
    aria-label={intl.get("extract.filterDate")}
  >
    {presets.map((preset) => (
      <button
        key={preset.id}
        type="button"
        className={`extract-date-range-calendar__preset-btn ${
          activePreset === preset.id ? "extract-date-range-calendar__preset-btn--active" : ""
        }`.trim()}
        onClick={() => onPresetClick(preset.id)}
        aria-pressed={activePreset === preset.id}
      >
        {intl.get(preset.labelKey)}
      </button>
    ))}
    {showClear ? (
      <button
        type="button"
        className="extract-date-range-calendar__preset-btn extract-date-range-calendar__preset-btn--clear"
        onClick={onClear}
      >
        {intl.get("words.clear")}
      </button>
    ) : null}
  </div>
);

export const DateRangeCalendar = ({
  dateFrom = null,
  dateTo = null,
  onChange,
  maxDate = new Date(),
  minDate = null,
  className = "",
  variant = "default",
  showDateInputs = true,
}) => {
  const isMobile = useMediaQuery("(max-width:639px)");
  const isModal = variant === "modal";
  const useDateInputs = showDateInputs;
  const useSingleCalendar = isMobile || isModal;
  const locale = getDateFnsLocale();
  const [startMonth, setStartMonth] = useState(() => dateFrom || dateTo || new Date());
  const [activePreset, setActivePreset] = useState(() => detectActivePreset(dateFrom, dateTo));

  const leftViewDate = startMonth;
  const rightViewDate = addMonths(startMonth, 1);
  const showInlineCalendar = useDateInputs
    ? false
    : !isModal || activePreset === DATE_PRESETS.CUSTOM;

  useEffect(() => {
    setActivePreset(detectActivePreset(dateFrom, dateTo));
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (dateFrom) {
      setStartMonth(dateFrom);
    } else if (dateTo) {
      setStartMonth(subMonths(dateTo, 1));
    }
  }, [dateFrom, dateTo]);

  const handleDaySelect = (day) => {
    if (!day) return;

    const selectedDay = startOfDay(day);

    if (minDate && isBefore(selectedDay, startOfDay(minDate))) return;
    if (maxDate && isAfter(selectedDay, startOfDay(maxDate))) return;

    setActivePreset(DATE_PRESETS.CUSTOM);

    if (!dateFrom || (dateFrom && dateTo)) {
      onChange({ dateFrom: selectedDay, dateTo: null });
      setStartMonth(selectedDay);
      return;
    }

    const rangeStart = startOfDay(dateFrom);

    if (isBefore(selectedDay, rangeStart)) {
      onChange({ dateFrom: selectedDay, dateTo: rangeStart });
      return;
    }

    onChange({ dateFrom: rangeStart, dateTo: selectedDay });
  };

  const applyPresetRange = useCallback(
    (presetId) => {
      if (presetId === DATE_PRESETS.TODAY) {
        const range = getTodayDateRange();
        setActivePreset(DATE_PRESETS.TODAY);
        onChange(range);
        setStartMonth(range.dateFrom);
        return;
      }

      if (presetId === DATE_PRESETS.LAST_7_DAYS) {
        const range = getLast7DaysDateRange();
        setActivePreset(DATE_PRESETS.LAST_7_DAYS);
        onChange(range);
        setStartMonth(range.dateFrom);
        return;
      }

      if (presetId === DATE_PRESETS.LAST_30_DAYS) {
        const range = getLast30DaysDateRange();
        setActivePreset(DATE_PRESETS.LAST_30_DAYS);
        onChange(range);
        setStartMonth(range.dateFrom);
      }
    },
    [onChange],
  );

  const handlePresetClick = (presetId) => {
    if (presetId === DATE_PRESETS.CUSTOM) {
      setActivePreset(DATE_PRESETS.CUSTOM);
      return;
    }

    applyPresetRange(presetId);
  };

  const handleClear = () => {
    setActivePreset(null);
    onChange({ dateFrom: null, dateTo: null });
  };

  const handleLeftMonthChange = (month) => {
    setStartMonth(month);
  };

  const handleRightMonthChange = (month) => {
    setStartMonth(subMonths(month, 1));
  };

  const handleFromPickerChange = (value) => {
    setActivePreset(DATE_PRESETS.CUSTOM);

    if (!value) {
      onChange({ dateFrom: null, dateTo });
      return;
    }

    const selectedDay = startOfDay(value);
    if (!isDateInBounds(selectedDay, minDate, maxDate)) return;

    if (dateTo && isAfter(selectedDay, startOfDay(dateTo))) {
      onChange({ dateFrom: selectedDay, dateTo: selectedDay });
      setStartMonth(selectedDay);
      return;
    }

    onChange({ dateFrom: selectedDay, dateTo });
    setStartMonth(selectedDay);
  };

  const handleToPickerChange = (value) => {
    setActivePreset(DATE_PRESETS.CUSTOM);

    if (!value) {
      onChange({ dateFrom, dateTo: null });
      return;
    }

    const selectedDay = startOfDay(value);
    if (!isDateInBounds(selectedDay, minDate, maxDate)) return;

    if (dateFrom && isBefore(selectedDay, startOfDay(dateFrom))) {
      onChange({ dateFrom: selectedDay, dateTo: selectedDay });
      setStartMonth(selectedDay);
      return;
    }

    onChange({ dateFrom, dateTo: selectedDay });
    setStartMonth(selectedDay);
  };

  const fromPickerMaxDate =
    dateTo && maxDate && isBefore(dateTo, startOfDay(maxDate)) ? dateTo : maxDate;
  const toPickerMinDate = dateFrom || minDate;
  const pickerPopperProps = {
    className: "extract-date-picker-popper",
    placement: "bottom-start",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 0],
        },
      },
      {
        name: "sameWidth",
        enabled: true,
        phase: "beforeWrite",
        requires: ["computeStyles"],
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        effect: ({ state }) => {
          state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
        },
      },
    ],
  };
  const pickerPaperProps = {
    className: "extract-date-picker-popper__paper",
    elevation: 0,
    square: true,
  };

  const rangeSummary = buildRangeSummary(dateFrom, dateTo);
  const renderDay = renderRangeDay(dateFrom, dateTo);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={locale}>
      <div
        className={`extract-date-range-calendar ${
          useSingleCalendar
            ? "extract-date-range-calendar--mobile"
            : "extract-date-range-calendar--dual"
        } ${isModal ? "extract-date-range-calendar--modal" : ""} ${
          useDateInputs ? "extract-date-range-calendar--with-inputs" : ""
        } ${className}`.trim()}
      >
        <PresetChipsRow
          presets={PRESET_OPTIONS}
          activePreset={activePreset}
          onPresetClick={handlePresetClick}
          onClear={handleClear}
        />

        {useDateInputs ? (
          <div className="extract-date-range-calendar__date-inputs">
            <DesktopDatePicker
              value={dateFrom}
              onChange={handleFromPickerChange}
              minDate={minDate || undefined}
              maxDate={fromPickerMaxDate}
              inputFormat="dd/MM/yyyy"
              renderDay={renderDay}
              PopperProps={pickerPopperProps}
              componentsProps={{ desktopPaper: pickerPaperProps }}
              renderInput={(params) => (
                <DatePickerField label={intl.get("extract.dateFrom")} {...params} />
              )}
            />
            <DesktopDatePicker
              value={dateTo}
              onChange={handleToPickerChange}
              minDate={toPickerMinDate || undefined}
              maxDate={maxDate}
              inputFormat="dd/MM/yyyy"
              renderDay={renderDay}
              PopperProps={pickerPopperProps}
              componentsProps={{ desktopPaper: pickerPaperProps }}
              renderInput={(params) => (
                <DatePickerField label={intl.get("extract.dateTo")} {...params} />
              )}
            />
          </div>
        ) : (
          <p className="extract-date-range-calendar__summary" aria-live="polite">
            {rangeSummary}
          </p>
        )}

        {showInlineCalendar ? (
          <div
            className={
              useSingleCalendar
                ? "extract-date-range-calendar__single-grid"
                : "extract-date-range-calendar__dual-grid"
            }
          >
            {useSingleCalendar ? (
              <CalendarPicker
                date={leftViewDate}
                onChange={handleDaySelect}
                onMonthChange={handleLeftMonthChange}
                minDate={minDate || undefined}
                maxDate={maxDate}
                renderDay={renderDay}
                disableHighlightToday={false}
                showDaysOutsideCurrentMonth
                className="extract-date-range-calendar__picker extract-date-range-calendar__picker--single"
              />
            ) : (
              <>
                <CalendarPicker
                  date={leftViewDate}
                  onChange={handleDaySelect}
                  onMonthChange={handleLeftMonthChange}
                  minDate={minDate || undefined}
                  maxDate={maxDate}
                  renderDay={renderDay}
                  disableHighlightToday={false}
                  showDaysOutsideCurrentMonth
                  className="extract-date-range-calendar__picker extract-date-range-calendar__picker--left"
                />
                <CalendarPicker
                  date={rightViewDate}
                  onChange={handleDaySelect}
                  onMonthChange={handleRightMonthChange}
                  minDate={minDate || undefined}
                  maxDate={maxDate}
                  renderDay={renderDay}
                  disableHighlightToday={false}
                  showDaysOutsideCurrentMonth
                  className="extract-date-range-calendar__picker extract-date-range-calendar__picker--right"
                />
              </>
            )}
          </div>
        ) : null}
      </div>
    </LocalizationProvider>
  );
};

export const formatDateRangeLabel = (dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return "";
  if (dateFrom && dateTo) {
    if (isSameDay(dateFrom, dateTo)) return format(dateFrom, "dd/MM/yyyy");
    return `${format(dateFrom, "dd/MM/yyyy")} — ${format(dateTo, "dd/MM/yyyy")}`;
  }
  return formatFilterDate(dateFrom || dateTo);
};

/** Min/max calendar dates from item timestamps (e.g. created_at). */
export const getItemsDateRangeLabel = (items = [], dateKey = "created_at") => {
  let minDate = null;
  let maxDate = null;

  items.forEach((item) => {
    const raw = item?.[dateKey];
    if (!raw) return;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return;
    const day = startOfDay(parsed);
    if (!minDate || isBefore(day, minDate)) minDate = day;
    if (!maxDate || isAfter(day, maxDate)) maxDate = day;
  });

  if (!minDate) return "";
  return formatDateRangeLabel(minDate, maxDate || minDate);
};

/** Human-readable period for extract/home: preset name, custom range, or emptyLabel. */
export const getDateRangePeriodLabel = (
  dateFrom,
  dateTo,
  { emptyLabel = "", includeDates = true } = {},
) => {
  if (!dateFrom && !dateTo) return emptyLabel;

  const range = formatDateRangeLabel(dateFrom, dateTo);
  const preset = detectActivePreset(dateFrom, dateTo);
  let named = range;
  if (preset === DATE_PRESETS.TODAY) named = intl.get("extract.periodToday");
  else if (preset === DATE_PRESETS.LAST_7_DAYS) named = intl.get("extract.period7Days");
  else if (preset === DATE_PRESETS.LAST_30_DAYS) named = intl.get("extract.period30Days");

  if (includeDates && named && range && named !== range) {
    return intl.get("extract.periodNamedWithRange", { name: named, range });
  }
  return named || range;
};
